<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">
              {{ $t("COMMON.EVENTS") }}
              <i class="fa fa-spinner fa-spin" v-if="loading"></i>
            </h3>
          </div>
          <div class="col-6 text-right">
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_EVENTS)"
              :objectType="'events'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </template>

      <div class="card-body">
        <div class="filters">
          <div class="filters-select">
            <service-center-selector
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS)"
              @serviceCenterChanged="
                (serviceCenterId) => (selectedServiceCenter = serviceCenterId)
              "
            />
            <establishment-selector
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
              @establishmentChanged="
                (establishmentId) => (selectedEstablishment = establishmentId)
              "
            />
            <cafeteria-selector
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CAFETERIAS)"
              @cafeteriaChanged="
                (cafeteriaId) => (selectedCafeteria = cafeteriaId)
              "
            />
            <el-select
              v-model="selectedCategory"
              :placeholder="`${$t('COMMON.CATEGORY')}`"
            >
              <el-option :value="null" :label="$t('COMMON.ALL_CATEGORIES')">
              </el-option>
              <el-option
                v-for="category in EVENT_CATEGORIES"
                :key="category"
                :value="category"
                :label="$t(`EVENTS.EVENT_CATEGORY_${category}`)"
              />
            </el-select>
          </div>
          <div class="filters-buttons">
            <base-button @click="showMyEvents">
              {{ $t("EVENTS.SHOW_MY_EVENTS") }}
            </base-button>
            <base-button @click="clearFilter">
              {{ $t("EVENTS.SHOW_ALL") }}
            </base-button>
          </div>
        </div>
        <div class="calendar-wrapper">
          <full-calendar
            :timeZone="$getTimeZone()"
            :events="events"
            :plugins="calendarPlugins"
            :editable="false"
            :locale="$i18n.locale"
            :defaultView="defaultView"
            :theme="false"
            :header="headerToolbar"
            :buttonText="buttonText"
            :defaultDate="defaultDate"
            :nextDayThreshold="$getTimeZoneDuration()"
            :datesRender="handleDatesSet"
            :eventRender="handleEventRender"
            contentHeight="auto"
            ref="fullCalendar"
            @dateClick="handleDayClick"
            @eventClick="handleEventClick"
          >
          </full-calendar>
        </div>
      </div>
    </card>

    <div class="reference">
      <div
        class="reference-item"
        v-for="category in EVENT_CATEGORIES"
        :key="category"
        :value="category"
      >
        <ul>
          <li
            :style="{ backgroundColor: EVENT_CATEGORIES_COLORS[category] }"
          ></li>
          <li>{{ $t(`EVENTS.EVENT_CATEGORY_${category}`) }}</li>
        </ul>
      </div>
    </div>

    <modal
      class="event-add-modal"
      :show="showEventFormModal"
      :showClose="true"
      @close="closeEventsModal"
      modal-classes="modal-secondary"
      size="xl"
    >
      <div class="modal-body-inner" v-if="!!eventModel">
        <div class="header">
          <h2>
            {{
              eventModel.id ? $t("EVENTS.EDIT_EVENT") : $t("EVENTS.ADD_EVENT")
            }}
          </h2>
          <button @click="closeEventsModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="body">
          <!-- Organization -->
          <base-input
            :label="`${$t('COMMON.ORGANIZATION')} (*)`"
            :placeholder="$t('COMMON.ORGANIZATION')"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          >
            <organization-selector
              :organization="eventModel.organization.id"
              :filterable="true"
              :showAll="false"
              @organizationChanged="
                (organizationId) => {
                  eventModel.organization.id = organizationId;
                }
              "
              :disabled="!!eventModel.id"
            />
          </base-input>

          <base-input
            :label="`${$t('COMMON.CATEGORY')} (*)`"
            :placeholder="$t('COMMON.CATEGORY')"
          >
            <el-select
              v-model="eventModel.category"
              :placeholder="`${$t('COMMON.CATEGORY')}`"
            >
              <el-option
                v-for="category in EVENT_CATEGORIES"
                :key="category"
                :value="category"
                :label="$t(`EVENTS.EVENT_CATEGORY_${category}`)"
              />
            </el-select>
          </base-input>

          <div class="row">
            <base-input
              class="col-6"
              :label="`${$t('EVENTS.STARTS_AT')} (*)`"
              :placeholder="$t('EVENTS.STARTS_AT')"
            >
              <flat-picker
                :config="{
                  allowInput: true,
                  locale: $flatPickrLocale(),
                  enableTime: true,
                }"
                class="form-control datepicker"
                v-model="eventModel.starts_at"
              >
              </flat-picker>
            </base-input>
            <base-input
              class="col-6"
              :label="`${$t('EVENTS.ENDS_AT')} (*)`"
              :placeholder="$t('EVENTS.ENDS_AT')"
            >
              <flat-picker
                :config="{
                  allowInput: true,
                  locale: $flatPickrLocale(),
                  enableTime: true,
                }"
                class="form-control datepicker"
                v-model="eventModel.ends_at"
              >
              </flat-picker>
            </base-input>
          </div>

          <base-input
            :label="`${$t('EVENTS.TITLE')} (*)`"
            :placeholder="$t('EVENTS.TITLE')"
            v-model="eventModel.title"
          >
          </base-input>

          <h2 class="mt-1">
            {{ `${$t("COMMON.EXCERPT")} (*)` }}
          </h2>
          <html-editor v-model="eventModel.excerpt"> </html-editor>

          <base-input
            :label="`${$t('COMMON.SERVICECENTER')} `"
            :placeholder="$t('COMMON.SERVICECENTER')"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS)"
          >
            <service-centers-selector
              :serviceCenters="eventModel.serviceCenters"
              :showAll="false"
              :filterOrganization="eventModel.organization.id"
              :disabled="eventModel.locked"
              @serviceCentersChanged="
                (serviceCenters) => {
                  eventModel.serviceCenters = serviceCenters;
                  eventModel.establishments = [];
                  eventModel.cafeterias = [];
                }
              "
            />
          </base-input>

          <base-input
            :label="`${$t('COMMON.ESTABLISHMENT')} `"
            :placeholder="$t('COMMON.ESTABLISHMENT')"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
          >
            <establishments-selector
              :establishments="eventModel.establishments"
              :onlyActive="true"
              :showAll="false"
              :filterOrganization="eventModel.organization.id"
              :filterServiceCenters="
                eventModel.serviceCenters?.map(
                  (serviceCenter) => serviceCenter.id
                )
              "
              :disabled="eventModel.serviceCenters.length === 0"
              @establishmentsChanged="
                (establishments) => {
                  eventModel.establishments = establishments;
                  eventModel.cafeterias = [];
                }
              "
            />
          </base-input>

          <base-input
            :label="`${$t('COMMON.CAFETERIA')} `"
            :placeholder="$t('COMMON.CAFETERIA')"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CAFETERIAS)"
          >
            <cafeterias-selector
              :cafeterias="eventModel.cafeterias"
              :filterable="true"
              :showAll="false"
              :multiple="true"
              :filterOrganization="eventModel.organization.id"
              :filterServiceCenters="
                eventModel.serviceCenters?.map(
                  (serviceCenter) => serviceCenter.id
                )
              "
              :filterEstablishments="
                eventModel.establishments?.map(
                  (establishment) => establishment.id
                )
              "
              :disabled="eventModel.establishments.length === 0"
              @cafeteriasChanged="
                (cafeterias) => {
                  eventModel.cafeterias = cafeterias;
                }
              "
            />
          </base-input>
        </div>
        <div class="footer">
          <base-button
            type="button"
            class="btn btn-sm btn-primary cancel"
            native-type="submit"
            @click="closeEventsModal"
          >
            {{ $t("COMMON.CANCEL") }}
          </base-button>
          <base-button
            v-if="!eventModel.id"
            type="button"
            class="btn btn-sm btn-primary add"
            native-type="submit"
            @click="handleAdd"
          >
            {{ $t("EVENTS.ADD_EVENT") }}
          </base-button>
          <base-button
            v-if="!!eventModel.id"
            type="button"
            class="btn btn-sm btn-primary edit"
            native-type="submit"
            @click="handleEdit"
          >
            {{ $t("EVENTS.EDIT_EVENT") }}
          </base-button>
        </div>
      </div>
    </modal>

    <modal
      class="event-view-modal"
      :show="showEventViewModal"
      @close="closeEventsModal"
      :showClose="true"
      modal-classes="modal-secondary"
      size="xl"
    >
      <div class="modal-body-inner" v-if="!!selectedEvent">
        <div
          :style="{ 'background-color': selectedEvent.color }"
          class="header"
        >
          <h2>
            {{ selectedEvent.title }}
          </h2>
          <button @click="closeEventsModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="body">
          <dl class="row">
            <dt class="col-sm-4">{{ $t("COMMON.CATEGORY") }}</dt>
            <dd class="col-sm-8">
              {{ $t(`EVENTS.EVENT_CATEGORY_${selectedEvent.category}`) }}
            </dd>
          </dl>

          <dl class="row" v-if="selectedEvent.serviceCenters.length > 0">
            <dt class="col-sm-4">{{ $t("COMMON.SERVICECENTER") }}</dt>
            <dd class="col-sm-8">
              <span
                v-for="(serviceCenter, index) in selectedEvent.serviceCenters"
                :key="serviceCenter.id"
              >
                <object-link :object="serviceCenter" />
                <span v-if="index &lt; selectedEvent.serviceCenters.length - 1">, </span>
              </span>
            </dd>
          </dl>

          <dl class="row" v-if="selectedEvent.establishments.length > 0">
            <dt class="col-sm-4">{{ $t("COMMON.ESTABLISHMENT") }}</dt>
            <dd class="col-sm-8">
              <span
                v-for="(establishment, index) in selectedEvent.establishments"
                :key="establishment.id"
              >
                <object-link :object="establishment" />
                <span v-if="index &lt; selectedEvent.establishments.length - 1">, </span>
              </span>
            </dd>
          </dl>

          <dl class="row" v-if="selectedEvent.cafeterias.length > 0">
            <dt class="col-sm-4">{{ $t("COMMON.CAFETERIA") }}</dt>
            <dd class="col-sm-8">
              <span
                v-for="(cafeteria, index) in selectedEvent.cafeterias"
                :key="cafeteria.id"
              >
                <object-link :object="cafeteria" />
                <span v-if="index &lt; selectedEvent.cafeterias.length - 1">, </span>
              </span>
            </dd>
          </dl>

          <dl class="row">
            <dt class="col-sm-4">{{ $t("EVENTS.STARTS_AT") }}</dt>
            <dd class="col-sm-8">
              {{ $timeZoneDateFormat(selectedEvent.starts_at) }}
            </dd>
          </dl>

          <dl class="row">
            <dt class="col-sm-4">{{ $t("EVENTS.ENDS_AT") }}</dt>
            <dd class="col-sm-8">
              {{ $timeZoneDateFormat(selectedEvent.ends_at) }}
            </dd>
          </dl>

          <dl class="row">
            <dt class="col-sm-4">{{ $t("COMMON.EXCERPT") }}</dt>
            <dd class="col-sm-8" v-html="selectedEvent.excerpt"></dd>
          </dl>

          <dl
            class="row"
            v-if="(selectedEvent.files || []).length > 0 || canManageEventFiles"
          >
            <dt class="col-sm-4">{{ $t("COMMON.FILES") }}</dt>
            <dd class="col-sm-8">
              <event-files
                :event="selectedEvent"
                :canManage="canManageEventFiles"
                @filesChanged="refreshSelectedEvent"
              />
            </dd>
          </dl>
        </div>
        <div class="footer">
          <base-button
            type="button"
            class="btn btn-sm btn-primary cancel"
            native-type="submit"
            @click="closeEventsModal"
          >
            {{ $t("COMMON.CANCEL") }}
          </base-button>
          <base-button
            v-if="$currentUserCan($permissions.PERM_EDIT_EVENTS)"
            type="button"
            class="btn btn-sm btn-primary edit"
            native-type="submit"
            @click="openEditForm"
          >
            {{ $t("EVENTS.EDIT_EVENT") }}
          </base-button>
          <base-button
            v-if="$currentUserCan($permissions.PERM_DELETE_EVENTS)"
            type="danger"
            class="btn btn-danger"
            native-type="submit"
            @click="deleteEvent"
          >
            {{ $t("EVENTS.DELETE_EVENT") }}
          </base-button>
        </div>
      </div>
    </modal>
  </div>
</template>
<script>
import { cloneDeep, debounce } from "lodash";
import moment from "moment";
import swal from "sweetalert2";
import format from "date-fns/format";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import permissions from "@/constants/permissions";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import defaultEvent from "./defaultEvent";
import EventFiles from "./partials/EventFiles.vue";
import { EVENT_CATEGORIES, EVENT_CATEGORIES_COLORS } from "@/constants/events";
import { Select, Option } from "element-ui";
import ServiceCentersSelector from "@/components/ServiceCentersSelector.vue";
import EstablishmentsSelector from "@/components/EstablishmentsSelector.vue";
import CafeteriasSelector from "@/components/CafeteriasSelector.vue";
import ServiceCenterSelector from "@/components/ServiceCenterSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import CafeteriaSelector from "@/components/CafeteriaSelector.vue";
import { mapGetters } from "vuex";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";

const today = new Date();
export default {
  name: "events-page",

  components: {
    FullCalendar,
    NotificationSubscription,
    flatPicker,
    HtmlEditor,
    OrganizationSelector,
    ServiceCentersSelector,
    EstablishmentsSelector,
    CafeteriasSelector,

    ServiceCenterSelector,
    EstablishmentSelector,
    CafeteriaSelector,
    EventFiles,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  computed: {
    ...mapGetters({
      me: "profile/me",
    }),

    canManageEventFiles() {
      return (
        this.$currentUserCan(permissions.PERM_CREATE_EVENTS) ||
        this.$currentUserCan(permissions.PERM_EDIT_EVENTS)
      );
    },
  },

  data() {
    return {
      calendarPlugins: [
        dayGridPlugin,
        timeGridPlugin,
        interactionPlugin,
        listPlugin,
        momentTimezonePlugin,
      ],
      defaultView: "dayGridMonth",
      year: today.getFullYear(),
      today: format(today, "MMM dd"),
      defaultDate: format(today, "yyyy-MM-dd"),
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,dayGridWeek,listMonth,listWeek",
      },
      buttonText: {
        today: this.$t("COMMON.TODAY"),
        month: this.$t("COMMON.MONTH"),
        week: this.$t("COMMON.WEEK"),
        day: this.$t("COMMON.DAY"),
        prev: this.$t("COMMON.PREV"),
        next: this.$t("COMMON.NEXT"),
        dayGridMonth: this.$t("EVENTS.GRID_MONTH"),
        dayGridWeek: this.$t("EVENTS.GRID_WEEK"),
        listMonth: this.$t("EVENTS.LIST_MONTH"),
        listWeek: this.$t("EVENTS.LIST_WEEK"),
      },
      events: [],
      selectedEvent: null,
      eventModel: null,
      showEventFormModal: false,
      showEventViewModal: false,
      loading: false,
      EVENT_CATEGORIES,
      EVENT_CATEGORIES_COLORS,
      selectedServiceCenter: null,
      selectedEstablishment: null,
      selectedCafeteria: null,
      selectedCategory: null,
      moment: moment,
      // Calendar view dates for optimized event fetching
      calendarStartDate: null,
      calendarEndDate: null,
    };
  },

  mounted() {
    // Initial load will be triggered by handleDatesSet when calendar initializes
    console.log("Component mounted, waiting for datesRender callback...");
    
    // Fallback: if datesRender callback doesn't work, manually get view after calendar is ready
    this.$nextTick(() => {
      setTimeout(() => {
        if (!this.calendarStartDate || !this.calendarEndDate) {
          console.log("datesRender callback not triggered, trying manual approach...");
          this.initializeCalendarDates();
        }
      }, 1000);
    });
  },

  methods: {
    // Fallback method to initialize calendar dates manually
    initializeCalendarDates() {
      try {
        const calendarApi = this.$refs.fullCalendar.getApi();
        const view = calendarApi.view;
        
        console.log("Manual calendar initialization with view:", view);
        
        this.calendarStartDate = view.activeStart;
        this.calendarEndDate = view.activeEnd;
        
        console.log("Calendar dates initialized manually:", {
          start: this.calendarStartDate,
          end: this.calendarEndDate
        });
        
        this.getList();
      } catch (error) {
        console.error("Failed to manually initialize calendar dates:", error);
        // Last resort: just call getList without date filtering
        this.getList();
      }
    },

    // Handle calendar view change to get visible date range (datesRender callback in v4)
    handleDatesSet(info) {
      console.log("datesRender callback triggered with:", info);
      
      // In FullCalendar v4, the datesRender callback provides a different structure
      const dateInfo = {
        start: info.view.activeStart,
        end: info.view.activeEnd
      };
      
      const previousStartDate = this.calendarStartDate;
      const previousEndDate = this.calendarEndDate;
      
      this.calendarStartDate = dateInfo.start;
      this.calendarEndDate = dateInfo.end;
      
      console.log("Calendar date range updated:", {
        start: dateInfo.start,
        end: dateInfo.end,
        viewType: info.view.type
      });
      
      // Only fetch events if dates have actually changed (to avoid duplicate calls)
      if (!previousStartDate || !previousEndDate || 
          previousStartDate.getTime() !== dateInfo.start.getTime() || 
          previousEndDate.getTime() !== dateInfo.end.getTime()) {
        console.log("Dates changed, fetching events for range:", dateInfo);
        this.getList();
      } else {
        console.log("Dates unchanged, skipping getList");
      }
    },

    async deleteEvent() {
      const confirmation = await swal.fire({
        title: this.$t("EVENTS.DELETE_THIS_EVENT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        const id = this.selectedEvent.id;
        if (confirmation.value === true) {
          await this.$store.dispatch("events/destroy", id);
          await this.getList();
          this.closeEventsModal();
          this.$notify({
            type: "success",
            message: this.$t("EVENTS.EVENT_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    handleDayClick({ date }) {
      if (!this.$currentUserCan(permissions.PERM_CREATE_EVENTS)) {
        return;
      }
      const selectedEvent = cloneDeep(defaultEvent);
      selectedEvent.starts_at = moment(date).format("YYYY-MM-DDT00:00:00");
      selectedEvent.ends_at = moment(date).format("YYYY-MM-DDT23:59:59");
      this.eventModel = selectedEvent;
      this.showEventFormModal = true;
    },

    handleEventClick({ event }) {
      const eventId = event.id;

      // Lecture seule pour qui peut seulement voir : Modifier et Supprimer sont masqués.
      if (!this.$currentUserCan(permissions.PERM_VIEW_EVENTS)) {
        return;
      }

      const events = this.$store.getters["events/list"];
      const selectedEvent = cloneDeep(
        events.find((item) => item.id == eventId)
      );

      this.selectedEvent = cloneDeep(selectedEvent);
      this.showEventViewModal = true;
    },

    openEditForm() {
      const eventData = cloneDeep(this.selectedEvent);
      eventData.starts_at = this.$momentTimeZoneDate(
        eventData.starts_at
      ).toDate();
      eventData.ends_at = this.$momentTimeZoneDate(eventData.ends_at).toDate();
      console.log(eventData);
      this.eventModel = eventData;
      this.showEventFormModal = true;
      this.showEventViewModal = false;
    },

    closeEventsModal() {
      this.selectedEvent = null;
      this.eventModel = null;
      this.showEventFormModal = false;
      this.showEventViewModal = false;
    },

    async getList() {
      // Prevent multiple simultaneous requests
      if (this.loading) return;
      
      this.loading = true;
      console.log("getList called with calendar dates:", {
        start: this.calendarStartDate,
        end: this.calendarEndDate
      });
      
      try {
        let params = {
          sort: "starts_at",
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
          },
          page: {
            number: 1,
            size: 500, // Reduced from 999 since we're filtering by date range
          },
          include:
            "organization,serviceCenters,establishments,cafeterias,files",
          fields: {
            files: "name,url,human_readable_size",
          },
        };

        // Add date range filtering based on calendar view
        if (this.calendarStartDate && this.calendarEndDate) {
          // Add buffer to catch events that might span across view boundaries
          // For month view, we add extra days to catch multi-day events
          const startBuffer = moment(this.calendarStartDate).subtract(7, 'days').format('YYYY-MM-DD');
          const endBuffer = moment(this.calendarEndDate).add(7, 'days').format('YYYY-MM-DD');
          
          console.log("Adding date filters:", { startBuffer, endBuffer });
          
          params.filter = {
            ...params.filter,
            // Use proper date range filtering - events that either start OR end within the range
            // or events that span across the entire range
            date_range_start: startBuffer,
            date_range_end: endBuffer,
          };
        } else {
          console.log("No calendar dates available, fetching without date filter");
        }

        // Apply location filters
        if (this.selectedServiceCenter) {
          params.filter.serviceCenter = this.selectedServiceCenter;
        }

        if (this.selectedEstablishment) {
          params.filter.establishment = this.selectedEstablishment;
        }

        if (this.selectedCafeteria) {
          params.filter.cafeteria = this.selectedCafeteria;
        }
        
        if (this.selectedCategory) {
          params.filter.category = this.selectedCategory;
        }

        console.log("API request params:", params);

        await this.$store.dispatch("events/list", params);
        const events = this.$store.getters["events/list"];
        
        console.log(`Fetched ${events.length} events`);
        
        // Transform events for FullCalendar
        this.events = events.map((event) => {
          // Debug logging for specific event (can be removed in production)
          if (event.id == "25") {
            console.log(
              "Event timezone conversion:",
              moment.tz(event.starts_at, "UTC").format("YYYY-MM-DDTHH:mm:ss"),
              this.$getUtcDateTimeString(event.starts_at)
            );
          }

          return {
            id: event.id,
            title: event.title,
            start: event.starts_at,
            end: event.ends_at,
            backgroundColor: event.color,
            // Store original event data for modal display
            extendedProps: {
              originalEvent: event
            }
          };
        });
        
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error("Error fetching events:", error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    handleEventRender({ event, el }) {
      const files = event.extendedProps.originalEvent?.files || [];
      if (files.length === 0) {
        return;
      }
      const title = el.querySelector(".fc-title, .fc-list-item-title");
      if (!title) {
        return;
      }
      const icon = document.createElement("i");
      icon.className = "fas fa-paperclip mr-1";
      title.prepend(icon);
    },

    async refreshSelectedEvent() {
      const eventId = this.selectedEvent.id;
      await this.getList();
      const event = this.$store.getters["events/list"].find(
        (item) => item.id == eventId
      );
      if (event && this.selectedEvent) {
        this.selectedEvent = cloneDeep(event);
      }
    },

    showMyEvents() {
      if (this.me.restrict_to_service_centers) {
        this.selectedServiceCenter = this.me.allowedServiceCenters();
      }
      if (this.me.restrict_to_establishments) {
        this.selectedEstablishment = this.me.allowedEstablishments();
      }
      if (this.me.restrict_to_cafeterias) {
        this.selectedCafeteria = this.me.allowedCafeterias();
      }
    },
    clearFilter() {
      this.selectedServiceCenter = null;
      this.selectedEstablishment = null;
      this.selectedCafeteria = null;
    },

    async handleAdd() {
      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });
      let eventData = cloneDeep(this.eventModel);
      eventData = this.$fillUserOrganizationData(eventData);
      eventData = this.$fillUserCafeteriasData(eventData);
      eventData.color = EVENT_CATEGORIES_COLORS[eventData.category];
      eventData.starts_at = this.$getUtcDateTimeString(eventData.starts_at);
      eventData.ends_at = this.$getUtcDateTimeString(eventData.ends_at);

      if (eventData.serviceCenter) {
        if (!eventData.serviceCenter.id) {
          delete eventData.serviceCenter;
        }
      }
      if (eventData.establishment) {
        if (!eventData.establishment.id) {
          delete eventData.establishment;
        }
      }
      if (eventData.cafeteria) {
        if (!eventData.cafeteria.id) {
          delete eventData.cafeteria;
        }
      }

      try {
        await this.$store.dispatch("events/add", eventData);
        this.$notify({
          type: "success",
          message: this.$t("EVENTS.EVENT_ADDED"),
        });
        this.getList();
        this.closeEventsModal();
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.formErrors = error.response.data.errors;
        swal.close();
      }
    },

    async handleEdit() {
      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });
      let eventData = cloneDeep(this.eventModel);
      eventData = this.$fillUserOrganizationData(eventData);
      eventData = this.$fillUserCafeteriasData(eventData);
      eventData.color = EVENT_CATEGORIES_COLORS[eventData.category];
      eventData.starts_at = this.$getUtcDateTimeString(eventData.starts_at);
      eventData.ends_at = this.$getUtcDateTimeString(eventData.ends_at);
      // Les pièces jointes se gèrent à part (EventFiles), jamais via la mise à jour de l'évènement.
      delete eventData.files;
      if (eventData.relationshipNames) {
        eventData.relationshipNames = eventData.relationshipNames.filter(
          (name) => name !== "files"
        );
      }

      if (eventData.serviceCenter) {
        if (!eventData.serviceCenter.id) {
          delete eventData.serviceCenter;
        }
      }
      if (eventData.establishment) {
        if (!eventData.establishment.id) {
          delete eventData.establishment;
        }
      }
      if (eventData.cafeteria) {
        if (!eventData.cafeteria.id) {
          delete eventData.cafeteria;
        }
      }

      try {
        await this.$store.dispatch("events/update", eventData);
        const event = this.$store.getters["events/event"];
        this.$notify({
          type: "success",
          message: this.$t("EVENTS.EVENT_UPDATED"),
        });
        await this.getList();
        this.selectedEvent = cloneDeep(
          this.$store.getters["events/list"].find(
            (item) => item.id == event.id
          ) || event
        );
        this.showEventFormModal = false;
        this.showEventViewModal = true;
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.formErrors = error.response.data.errors;
        swal.close();
      }
    },
  },

  watch: {
    selectedServiceCenter: {
      handler: function() {
        // Only call debounced getList if calendar dates are available
        if (this.calendarStartDate && this.calendarEndDate) {
          console.log("Service center filter changed, calling debounced getList");
          this.debouncedGetList();
        }
      },
    },
    selectedEstablishment: {
      handler: function() {
        if (this.calendarStartDate && this.calendarEndDate) {
          console.log("Establishment filter changed, calling debounced getList");
          this.debouncedGetList();
        }
      },
    },
    selectedCafeteria: {
      handler: function() {
        if (this.calendarStartDate && this.calendarEndDate) {
          console.log("Cafeteria filter changed, calling debounced getList");
          this.debouncedGetList();
        }
      },
    },
    selectedCategory: {
      handler: function() {
        if (this.calendarStartDate && this.calendarEndDate) {
          console.log("Category filter changed, calling debounced getList");
          this.debouncedGetList();
        }
      },
    },
  },

  created() {
    // Create a debounced version of getList to limit API calls
    this.debouncedGetList = debounce(this.getList, 300);
  },
};
</script>

<style lang="scss">
@import "~@fullcalendar/core/main.css";
@import "~@/assets/sass/core/vendors/fullcalendar";
</style>
